import { useState } from "react";
import Modal, { ModalRoot, ModalTrigger } from "../Modal";
import Button from "@/components/bricks/button/Button";
import DownloadIcon from "../../icons/DownloadIcon";
import { FormikDatePicker } from "../../formcontrols/DatePicker";
import { Formik } from "formik";
import { DOWNLOAD_DATE_VALIDATION_SCHEMA } from "../../../constants/schemas";
import { dateToyyyyMMdd } from "../../../util/date";
import { StickyFooter } from "../../stickyFooter/modalStickyFooter";
import DataDlIcon from "../../icons/DataDlIcon";
import { Dialog } from "radix-ui";
import { addDays } from "date-fns";
import { openReportWithAuth } from "../../../api/util";

interface IDownloadModalProps {
  title: string;
  description: string;
  link: string;
  fromDateParam?: boolean;
  toDateParam?: boolean;
}

export const DownloadModal = ({
  config,
  close,
}: {
  config: IDownloadModalProps;
  close: () => void;
}) => {
  const { title, description } = config;

  const initialValues: {
    fromDate: Date | null;
    toDate: Date | null;
  } = {
    fromDate: null,
    toDate: null,
  };

  return (
    <Modal
      title={
        <>
          <DataDlIcon width={18} />
          {title}
        </>
      }
    >
      <Formik
        initialValues={initialValues}
        onSubmit={async (values) => {
          try {
            const params = new URLSearchParams();
            if (config.fromDateParam && values.fromDate) {
              params.append("fromDate", dateToyyyyMMdd(values.fromDate));
            }
            if (config.toDateParam && values.toDate) {
              const toDateAppendOneDay = addDays(values.toDate, 1);
              params.append("toDate", dateToyyyyMMdd(toDateAppendOneDay));
            }
            const parameterString = params.toString()
              ? `?${params.toString()}`
              : "";
            await openReportWithAuth(`${config.link}${parameterString}`);
            close();
          } catch (error) {
            console.error(error);
          }
        }}
        validationSchema={
          config.fromDateParam && config.toDateParam
            ? DOWNLOAD_DATE_VALIDATION_SCHEMA
            : undefined
        }
        enableReinitialize
      >
        {(props) => {
          return (
            <div className="c-modal-container">
              <div className="c-modal-container-inner c-item-list">
                {description && (
                  <p className="alert alert-info p-3 flex-100 rounded">
                    {description}
                  </p>
                )}
                {(config.fromDateParam || config.toDateParam) && (
                  <h3 className="h4 flex-100 mb-n2 mt-2">Definér udtræk</h3>
                )}
                {config.fromDateParam && (
                  <div>
                    <FormikDatePicker
                      formik={props}
                      config={{
                        component: "datepicker",
                        id: "fromDate",
                        label: "Fra dato",
                        isRequired: true,
                      }}
                    />
                  </div>
                )}
                {config.toDateParam && (
                  <div>
                    <FormikDatePicker
                      formik={props}
                      config={{
                        component: "datepicker",
                        id: "toDate",
                        label: "Til dato",
                        isRequired: true,
                      }}
                    />
                  </div>
                )}
              </div>
              <StickyFooter>
                <Dialog.Close className="btn btn-secondary">
                  Annuller
                </Dialog.Close>
                <Button
                  color="primary"
                  disabled={!props.isValid || props.isSubmitting}
                  type="submit"
                  onClick={() => {
                    props.handleSubmit();
                  }}
                >
                  <DownloadIcon /> Hent
                </Button>
              </StickyFooter>
            </div>
          );
        }}
      </Formik>
    </Modal>
  );
};

export const DownloadModalWithRoot = ({
  disabled,
  config,
  modalTrigger,
}: {
  disabled?: boolean;
  config: IDownloadModalProps;
  modalTrigger?: React.ReactElement;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <ModalRoot
      controller={{
        open,
        setOpen,
      }}
    >
      <ModalTrigger asChild>
        {modalTrigger ? (
          modalTrigger
        ) : (
          <Button
            disabled={disabled}
            color="primary"
            icon
            transparent
            className="tooltip-center"
            aria-label="Hent udtræk..."
          >
            <span className="icon-bg">
              <DownloadIcon />
            </span>
          </Button>
        )}
      </ModalTrigger>
      <DownloadModal config={config} close={() => setOpen(false)} />
    </ModalRoot>
  );
};
